import React from 'react';
import { UserRole } from '@citrite/sf-api';
import { documentGenerationPiletExtensionSlots } from '@sharefiledev/doc-gen-pilet-sdk';
import { PiletApi, PiletUserContext } from '@sharefiledev/sharefile-appshell';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { addBlocks } from './blocks';
import {
	createDrawerBaseClassName,
	detailsDrawerBaseClassName,
	signatureRequestDrawerBaseClassName,
} from './common/componentUtils';
import { useAppStore } from './common/store';
import {
	CreateDocumentTemplateExtensionModalProps,
	getCreateDocumentTemplateModalProps,
} from './components/CreateDocumentTemplateModal';
import { CreateTemplateSlot } from './components/CreateTemplate';
import {
	AsyncLazyEditDesktopAppModalBody,
	AsyncLazySignInToShareFileModalBody,
	EditDesktopAppModalFooter,
	EditDesktopAppModalTitle,
	SignInToShareFileModalTitle,
} from './components/EditDesktopAppModal';
import Footer from './components/Footer';
import Header from './components/Header';
import { getUploadDocxFileModalProps } from './components/UploadDocxFileModal';
import {
	onUploadDocxFileModalCancel,
	UploadDocxFileModalProps,
} from './components/UploadDocxFileModal/UploadDocxFileModal';
import {
	DocGenExtensionSlots,
	EditDesktopModalExtensionSlotName,
	SignIntoShareFileModalExtensionSlotName,
} from './constants';
import { acquireAccessToken } from './data/acquireAccessToken';
import { configureDocgenClient } from './data/docgenClient';
import { getDGConfig, isLocalDGSetup } from './data/docgenConfiguration';
import { setLogger } from './logger';
import { onDocumentTemplateClose } from './pages/CreateDocumentTemplate';
import SignatureRequestFooter from './pages/SignatureRequest/SignatureRequestFooter';
import SignatureRequestHeader from './pages/SignatureRequest/SignatureRequestHeader';
import { templateDetailsRoutePath } from './routes';
import { CoreExtensionParams, CoreExtensionSlotParams } from './types';
import { getOverlayDrawerProps } from './utils';

const LazyCreateDocumentTemplateExtensionModal = React.lazy(() =>
	import('./components/CreateDocumentTemplateModal').then(module => ({
		default: module.CreateDocumentTemplateExtensionModal,
	}))
);
const AsyncLazyCreateDocumentTemplateExtensionModal: React.FC<
	CreateDocumentTemplateExtensionModalProps
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyCreateDocumentTemplateExtensionModal {...props} />
		</React.Suspense>
	);
};

const LazyUploadDocxFileModal = React.lazy(
	() => import('./components/UploadDocxFileModal')
);
const AsyncLazyUploadDocxFileModal: React.FC<
	CoreExtensionSlotParams<UploadDocxFileModalProps>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyUploadDocxFileModal {...props} />
		</React.Suspense>
	);
};

const LazyCreateTemplateDrawer = React.lazy(
	() => import('./pages/CreateDocumentTemplate')
);
const AsyncLazyCreateDocumentTemplate: React.FC<CoreExtensionSlotParams<any>> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyCreateTemplateDrawer {...props} />
		</React.Suspense>
	);
};

const LazySignatureRequestDrawer = React.lazy(() => import('./pages/SignatureRequest'));
const AsyncLazySignatureRequestDrawer: React.FC<CoreExtensionSlotParams<any>> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazySignatureRequestDrawer {...props} />
		</React.Suspense>
	);
};

const LazyTemplatesPiletRegisteredDrawer = React.lazy(() =>
	import('./pages/Details').then(module => ({
		default: module['TemplatesPiletRegisteredDrawer'],
	}))
);
const AsyncLazyTemplatesPiletRegisteredDrawer: React.FC<
	CoreExtensionSlotParams<any>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyTemplatesPiletRegisteredDrawer {...props} />
		</React.Suspense>
	);
};

const LazyDetails = React.lazy(() =>
	import('./pages/Details').then(module => ({ default: module['Details'] }))
);
const AsyncLazyDetails: React.FC<CoreExtensionSlotParams<any>> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyDetails {...props} />
		</React.Suspense>
	);
};

interface TemplateSelectionInEsignExtensionParams extends CoreExtensionParams {
	setDocumentGenerationDrawerContext: () => void;
	containerRID?: string;
	redirectPath?: string;
}

const LazyTemplateSelectionInEsign = React.lazy(() =>
	import('./pages/TemplateSelectionInEsign').then(module => ({
		default: module['TemplateSelectionInEsign'],
	}))
);
const AsyncLazyTemplateSelectionInEsign: React.FC<
	TemplateSelectionInEsignExtensionParams
> = props => {
	const { setDocumentGenerationDrawerContext, app, containerRID, redirectPath } = props;
	const queryClient = new QueryClient();
	return (
		<React.Suspense fallback={<Skeleton />}>
			<QueryClientProvider client={queryClient}>
				<LazyTemplateSelectionInEsign
					app={app}
					setDocumentGenerationDrawerContext={setDocumentGenerationDrawerContext}
					containerRID={containerRID}
					redirectPath={redirectPath}
				/>
			</QueryClientProvider>
		</React.Suspense>
	);
};

export function setup(app: PiletApi) {
	setLogger(app.sf.getLogger());

	const sfUserContext: PiletUserContext = app.sf.piletUserContext.get();

	const user = sfUserContext.user;
	const isEmployee = user.Roles?.includes(UserRole.Employee);

	if (sfUserContext) {
		useAppStore.getState().setSFUserContext(sfUserContext);
	}

	if (isEmployee) {
		registerExtensions(app);

		if (isLocalDGSetup()) {
			const dgConfig = getDGConfig();

			configureDocgenClient({
				baseUrl: dgConfig.DOCGEN_BASE_URL,
				useBffAuthProxy: false,
				getAccessToken: () => acquireAccessToken(dgConfig.CLIENT_ID),
			});
		} else {
			let appOrigin = window.origin;
			if (typeof app.meta.config.apiOrigin === 'string') {
				appOrigin = app.meta.config.apiOrigin;
			}
			configureDocgenClient({
				baseUrl: `${appOrigin}/io/docgen`,
				useBffAuthProxy: true,
			});
		}

		app.registerPage(templateDetailsRoutePath, function () {
			return <AsyncLazyDetails app={app} />;
		});

		app.registerDrawer(
			'urn:sfmodal:docgen:modals:document-template-send-signature-request',
			props => {
				return <AsyncLazySignatureRequestDrawer {...props} app={app} />;
			},
			{
				drawerProps: {
					title: <SignatureRequestHeader />,
					footer: <SignatureRequestFooter app={app} />,
					...getOverlayDrawerProps({
						baseClassName: signatureRequestDrawerBaseClassName,
					}),
				},
			}
		);

		app.registerDrawer(
			'urn:sfmodal:docgen:modals:document-template-details',
			props => {
				return <AsyncLazyTemplatesPiletRegisteredDrawer {...props} app={app} />;
			},
			{
				drawerProps: getOverlayDrawerProps({
					isFullScreen: false,
					baseClassName: detailsDrawerBaseClassName,
				}),
			}
		);

		app.registerDrawer(
			DocGenExtensionSlots.CREATE_DOCUMENT_TEMPLATE_DRAWER,
			props => {
				return <AsyncLazyCreateDocumentTemplate {...props} app={app} />;
			},
			{
				drawerProps: {
					title: <Header />,
					footer: <Footer />,
					...getOverlayDrawerProps({
						baseClassName: createDrawerBaseClassName,
					}),
				},
				onCancelInteraction: () => {
					return onDocumentTemplateClose();
				},
			}
		);

		app.registerModal(
			documentGenerationPiletExtensionSlots.DocumentTemplateCreate,
			props => {
				return <AsyncLazyCreateDocumentTemplateExtensionModal app={app} {...props} />;
			},
			{
				modalProps: {
					...getCreateDocumentTemplateModalProps(),
				},
			}
		);

		app.registerModal(
			DocGenExtensionSlots.UPLOAD_DOCX_FILE_MODAL,
			props => {
				return <AsyncLazyUploadDocxFileModal app={app} {...props} />;
			},
			{
				modalProps: {
					...getUploadDocxFileModalProps(),
				},
				onCancelInteraction: () => {
					return onUploadDocxFileModalCancel();
				},
			}
		);

		app.registerExtension('urn:sfextslot:docgen:document-template-list', ({ params }) => (
			<AsyncLazyTemplateSelectionInEsign app={app} {...params} />
		));

		app.registerModal(
			EditDesktopModalExtensionSlotName,
			props => <AsyncLazyEditDesktopAppModalBody app={app} {...props} />,
			{
				modalProps: {
					title: <EditDesktopAppModalTitle />,
					footer: <EditDesktopAppModalFooter />,
					centered: true,
				},
				onCompleteInteraction: () => {
					const { isSignedInToShareFileLinkClicked, resetStore } = useAppStore.getState();
					if (!isSignedInToShareFileLinkClicked) {
						return resetStore();
					}
				},
				onCancelInteraction: () => {
					return Promise.resolve().then(() => {
						const { isSignedInToShareFileLinkClicked, resetStore } =
							useAppStore.getState();
						if (!isSignedInToShareFileLinkClicked) {
							return resetStore();
						}
					});
				},
				disableHistoryEffects: true,
			}
		);

		app.registerModal(
			SignIntoShareFileModalExtensionSlotName,
			props => <AsyncLazySignInToShareFileModalBody app={app} {...props} />,
			{
				modalProps: {
					title: <SignInToShareFileModalTitle />,
					footer: null,
					centered: true,
				},
				onCompleteInteraction: () => {
					const { setIsSignedInToShareFileLinkClicked } = useAppStore.getState();
					setIsSignedInToShareFileLinkClicked(false);
				},
				onCancelInteraction: async () => {
					const { resetStore } = useAppStore.getState();
					return resetStore();
				},
				disableHistoryEffects: true,
			}
		);
	}

	addBlocks(app);
}

function registerExtensions(app: PiletApi) {
	CreateTemplateSlot(app, 'urn:sfextslot:sharefile-appshell:dashboard:shortcuts');
	CreateTemplateSlot(app, 'urn:sfextslot:sharefile-contentviewer:context-actions', 900);
	CreateTemplateSlot(
		app,
		'urn:sfextslot:sharefile-appshell:folder:item-context-actions',
		300
	);
}
